import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  BarChart3,
  Check,
  ChevronRight,
  FolderPlus,
  ImagePlus,
  LogOut,
  Music,
  Pencil,
  Play,
  Plus,
  Save,
  Square,
  Trash2,
  UploadCloud,
  UserCog,
  Users,
  Gamepad2,
  Trophy,
} from "lucide-react";

type Category = {
  id: number;
  name: string;
  description: string;
  active: number;
  meme_count: number;
};
type Zone = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  placeholder: string;
  color: string;
  backgroundEnabled: boolean;
  backgroundColor: string;
  backgroundOpacity: number;
  maxFontSize: number;
  maxChars: number;
  maxLines: number;
  uppercase: boolean;
  required: boolean;
};
type Template = {
  id: string;
  title: string;
  optimized_key: string;
  category_ids: string | null;
  active: number;
  width: number;
  height: number;
  zones: Zone[];
};
type Sound = {
  id: string;
  title: string;
  object_key: string;
  active: number;
  very_good: number;
  meh: number;
  very_bad: number;
  adult: number;
};
type AdminUser = { email:string; role:"owner"|"admin"; active:number; created_by:string|null; created_at:string };
type CurrentUser = { email:string; role:"owner"|"admin" };
type DailyReport = { day:string; unique_visitors:number; games_started:number; games_completed:number; players_started:number };
const api = "/api/admin/yonetim-x7k9";
const nativeFetch = globalThis.fetch.bind(globalThis);
const fetch: typeof globalThis.fetch = (input, init = {}) => {
  const method = (init.method ?? "GET").toUpperCase();
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  if (
    url.startsWith(api) &&
    !["GET", "HEAD", "OPTIONS"].includes(method)
  ) {
    const headers = new Headers(init.headers);
    headers.set("x-mm-admin-request", "1");
    init = { ...init, headers };
  }
  return nativeFetch(input, init);
};
const backgroundPalette = [
  "#000000",
  "#ffffff",
  "#ef4444",
  "#f97316",
  "#facc15",
  "#22c55e",
  "#06b6d4",
  "#3b82f6",
  "#7957ff",
  "#ec4899",
];
const newZone = (): Zone => ({
  id: crypto.randomUUID(),
  x: 0.1,
  y: 0.08,
  width: 0.8,
  height: 0.22,
  placeholder: "Metnini yaz",
  color: "#ffffff",
  backgroundEnabled: false,
  backgroundColor: "#000000",
  backgroundOpacity: 0.85,
  maxFontSize: 64,
  maxChars: 120,
  maxLines: 3,
  uppercase: true,
  required: true,
});
const rgba = (hex: string, opacity: number) => {
  const value = hex.replace("#", "");
  if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(value))
    return `rgba(0,0,0,${opacity})`;
  const n = Number.parseInt(
    value.length === 3
      ? value
          .split("")
          .map((x) => x + x)
          .join("")
      : value,
    16,
  );
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${opacity})`;
};

export function Admin() {
  const [logged, setLogged] = useState(false),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]),
    [templates, setTemplates] = useState<Template[]>([]),
    [sounds, setSounds] = useState<Sound[]>([]),
    [admins, setAdmins] = useState<AdminUser[]>([]),
    [reports, setReports] = useState<DailyReport[]>([]),
    [currentUser, setCurrentUser] = useState<CurrentUser | null>(null),
    [selected, setSelected] = useState<number | null>(null),
    [section, setSection] = useState<"reports" | "categories" | "sounds" | "users">("reports"),
    [adding, setAdding] = useState(false);
  const load = async () => {
    try {
      let r=await fetch(`${api}/dashboard`);
      for(let attempt=0;attempt<2&&!r.ok&&[401,403].includes(r.status);attempt++){
        await new Promise(resolve=>setTimeout(resolve,500*(attempt+1)));
        r=await fetch(`${api}/dashboard`);
      }
      if(!r.ok){
        const d=await r.json().catch(()=>({})) as {error?:string};
        setError(d.error??"Bu e-posta için yönetici yetkisi bulunamadı.");
        setLogged(false);
        return;
      }
      const d = (await r.json()) as {
        categories: Category[]; templates: Template[]; sounds: Sound[];
        admins: AdminUser[]; reports: DailyReport[]; currentUser: CurrentUser;
      };
      setCategories(d.categories); setTemplates(d.templates); setSounds(d.sounds??[]);
      setReports(d.reports??[]);
      setAdmins(d.admins??[]); setCurrentUser(d.currentUser); setLogged(true); setError("");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  if (!logged)
    return (
      <main className="admin-login">
        <div className="admin-access-state">
          <span className="brand-mark">M!</span>
          <small>CLOUDFLARE ACCESS</small>
          <h1>{loading?"Yetki kontrol ediliyor":"Erişim kapalı"}</h1>
          <p>{loading?"Doğrulanmış Cloudflare kimliğiniz kontrol ediliyor.":"Bu e-posta yönetici listesinde aktif değil."}</p>
          {error && <p className="error">{error}</p>}
          {!loading&&<a className="primary" href="/cdn-cgi/access/logout">Farklı e-posta ile giriş yap</a>}
        </div>
      </main>
    );
  const category = categories.find((c) => c.id === selected);
  return (
    <main className="admin-shell">
      <aside>
        <div className="admin-brand">
          <span className="brand-mark">M!</span>
          <b>Meydan Yönetim</b>
        </div>
        <nav>
          <button className={section==="reports"?"active":""} onClick={() => {setSection("reports");setSelected(null)}}>
            <BarChart3 /> Raporlar
          </button>
          <button className={section==="categories"?"active":""} onClick={() => {setSection("categories");setSelected(null)}}>
            <FolderPlus />
            Kategoriler
          </button>
          <button className={section==="sounds"?"active":""} onClick={() => {setSection("sounds");setSelected(null)}}>
            <Music />
            Sesler
          </button>
          {currentUser?.role==="owner"&&<button className={section==="users"?"active":""} onClick={() => {setSection("users");setSelected(null)}}>
            <UserCog /> Yöneticiler
          </button>}
        </nav>
        <button
          className="logout"
          onClick={() => { location.href="/cdn-cgi/access/logout"; }}
        >
          <LogOut />
          Çıkış
        </button>
      </aside>
      <section className="admin-content">
        <header>
          <div>
            {category && (
              <button className="admin-back" onClick={() => setSelected(null)}>
                <ArrowLeft /> Kategoriler
              </button>
            )}
            <small>MEME MEYDANI</small>
            <h1>{section==="reports"?"Günlük raporlar":section==="sounds"?"Sesler":section==="users"?"Yöneticiler":category?.name ?? "Kategoriler"}</h1>
          </div>
          <span className="admin-ok">
            <Check /> Sistem canlı
          </span>
        </header>
        {section==="reports"?<ReportManager reports={reports}/>:section==="users"?<AdminUserManager users={admins} currentUser={currentUser!} onSaved={load}/>:section==="sounds"?<SoundManager sounds={sounds} onSaved={load}/>:!category ? (
          <CategoryHome
            rows={categories}
            onSelect={setSelected}
            adding={adding}
            setAdding={setAdding}
            onSaved={load}
          />
        ) : (
          <CategoryDetail
            category={category}
            templates={templates}
            onSaved={load}
          />
        )}
      </section>
    </main>
  );
}

function ReportManager({reports}:{reports:DailyReport[]}) {
  const todayKey=new Intl.DateTimeFormat("en-CA",{timeZone:"Europe/Berlin",year:"numeric",month:"2-digit",day:"2-digit"}).format(new Date());
  const today=reports.find(row=>row.day===todayKey)??{day:todayKey,unique_visitors:0,games_started:0,games_completed:0,players_started:0};
  const recent=[...reports].slice(0,14).reverse();
  const max=Math.max(1,...recent.flatMap(row=>[Number(row.unique_visitors),Number(row.games_started)]));
  const cards=[
    {label:"Bugünkü ziyaretçi",value:today.unique_visitors,icon:<Users/>},
    {label:"Başlatılan oyun",value:today.games_started,icon:<Gamepad2/>},
    {label:"Tamamlanan oyun",value:today.games_completed,icon:<Trophy/>},
    {label:"Oyuna başlayan kişi",value:today.players_started,icon:<BarChart3/>},
  ];
  return <div className="report-page">
    <div className="report-cards">{cards.map(card=><article className="admin-card report-stat" key={card.label}><span>{card.icon}</span><div><small>{card.label}</small><strong>{Number(card.value).toLocaleString("tr-TR")}</strong></div></article>)}</div>
    <section className="admin-card report-chart"><div className="report-title"><div><h2>Son 14 gün</h2><p>Mor: benzersiz ziyaretçi · Yeşil: başlatılan oyun</p></div></div>
      {recent.length?<div className="report-bars">{recent.map(row=><div className="report-day" key={row.day} title={`${row.day}: ${row.unique_visitors} ziyaretçi, ${row.games_started} oyun`}><div className="bar-pair"><i style={{height:`${Math.max(3,Number(row.unique_visitors)/max*100)}%`}}/><i style={{height:`${Math.max(3,Number(row.games_started)/max*100)}%`}}/></div><small>{row.day.slice(5)}</small></div>)}</div>:<p className="admin-empty">İstatistikler bugünden itibaren burada birikecek.</p>}
    </section>
    <section className="admin-card report-table-card"><h2>Günlük döküm</h2><div className="report-table-wrap"><table className="report-table"><thead><tr><th>Tarih</th><th>Ziyaretçi</th><th>Başlayan oyun</th><th>Biten oyun</th><th>Oyuncu</th></tr></thead><tbody>{reports.map(row=><tr key={row.day}><td>{new Intl.DateTimeFormat("tr-TR",{day:"2-digit",month:"short",year:"numeric",timeZone:"Europe/Berlin"}).format(new Date(`${row.day}T12:00:00Z`))}</td><td>{row.unique_visitors}</td><td>{row.games_started}</td><td>{row.games_completed}</td><td>{row.players_started}</td></tr>)}</tbody></table></div></section>
  </div>;
}

function AdminUserManager({users,currentUser,onSaved}:{users:AdminUser[];currentUser:CurrentUser;onSaved:()=>Promise<void>}) {
  const [email,setEmail]=useState(""),[role,setRole]=useState<"admin"|"owner">("admin"),[message,setMessage]=useState(""),[busy,setBusy]=useState(false);
  const add=async(e:React.FormEvent)=>{
    e.preventDefault();setBusy(true);setMessage("");
    const r=await fetch(`${api}/users`,{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify({email,role})});
    const d=await r.json().catch(()=>({})) as {error?:string};setBusy(false);
    if(!r.ok){setMessage(d.error??"Kullanıcı eklenemedi.");return;}
    setEmail("");setMessage("Yönetici eklendi. Bu e-posta Cloudflare koduyla giriş yapabilir.");await onSaved();
  };
  const update=async(user:AdminUser,body:{active?:boolean;role?:string})=>{
    const r=await fetch(`${api}/users/${encodeURIComponent(user.email)}`,{method:"PATCH",headers:{"content-type":"application/json"},body:JSON.stringify(body)});
    const d=await r.json().catch(()=>({})) as {error?:string};
    if(!r.ok){setMessage(d.error??"Değişiklik kaydedilemedi.");return;}
    setMessage("Yetki güncellendi.");await onSaved();
  };
  return <div className="admin-users">
    <form className="admin-card admin-user-add" onSubmit={add}>
      <div><h2>Yeni yönetici</h2><p>E-posta izin listesine eklenir; ayrıca şifre oluşturulmaz.</p></div>
      <label>E-posta<input type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="kullanici@example.com"/></label>
      <label>Rol<select value={role} onChange={e=>setRole(e.target.value as "admin"|"owner")}><option value="admin">Yönetici</option><option value="owner">Sahip</option></select></label>
      <button className="primary" disabled={busy}><Plus/> Ekle</button>
    </form>
    {message&&<p className="admin-message">{message}</p>}
    <div className="admin-card admin-user-list">
      <h2>Yetkili e-postalar</h2>
      {users.map(user=><div key={user.email}>
        <div><b>{user.email}</b><small>{user.email===currentUser.email?"Şu anki hesabınız":user.created_by?`${user.created_by} ekledi`:"Sistem sahibi"}</small></div>
        <select value={user.role} disabled={user.email===currentUser.email} onChange={e=>void update(user,{role:e.target.value})}><option value="admin">Yönetici</option><option value="owner">Sahip</option></select>
        <button className={`switch ${user.active?"on":""}`} disabled={user.email===currentUser.email} onClick={()=>void update(user,{active:!user.active})} aria-label={user.active?"Devre dışı bırak":"Etkinleştir"}><i/></button>
      </div>)}
    </div>
  </div>;
}

function SoundManager({
  sounds,
  onSaved,
}: {
  sounds: Sound[];
  onSaved: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [playing, setPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const upload = async (files: File[]) => {
    setBusy(true);
    setMessage("");
    let added = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("audio", file);
      form.append("title", file.name.replace(/\.[^.]+$/, ""));
      const r = await fetch(`${api}/sounds`, { method: "POST", body: form });
      if (r.ok) added++;
      else {
        const d = (await r.json()) as { error?: string };
        setMessage(d.error ?? "Ses yüklenemedi.");
      }
    }
    setBusy(false);
    if (added) {
      setMessage(`${added} MP3 yüklendi.`);
      await onSaved();
    }
  };
  const patch = async (
    sound: Sound,
    key: "active" | "very_good" | "meh" | "very_bad" | "adult",
    value: boolean,
  ) => {
    await fetch(`${api}/sounds/${sound.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ [key]: value }),
    });
    await onSaved();
  };
  const normalize = async () => {
    if (
      !confirm(
        "Orta ve Çok kötü atamaları birleştirilsin, atanmamış bütün sesler kalıcı olarak silinsin mi?",
      )
    )
      return;
    setBusy(true);
    let total = 0,
      remaining = 1;
    while (remaining > 0) {
      const r = await fetch(`${api}/sounds/normalize`, { method: "POST" });
      if (!r.ok) {
        setMessage("Temizlik sırasında hata oluştu.");
        break;
      }
      const data = (await r.json()) as { deleted: number; remaining: number };
      total += data.deleted;
      remaining = data.remaining;
      setMessage(`${total} atanmamış ses silindi; ${remaining} kaldı…`);
    }
    setBusy(false);
    setMessage(`${total} atanmamış ses silindi. Orta ve Çok kötü eşitlendi.`);
    await onSaved();
  };
  const togglePlay = (sound: Sound) => {
    if (playing === sound.id) {
      audioRef.current?.pause();
      if (audioRef.current) audioRef.current.currentTime = 0;
      setPlaying(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(`/media/${sound.object_key}`);
    audioRef.current = audio;
    audio.onended = () => setPlaying(null);
    void audio.play();
    setPlaying(sound.id);
  };
  useEffect(
    () => () => {
      audioRef.current?.pause();
    },
    [],
  );
  return (
    <>
      <div className="category-toolbar sound-toolbar">
        <div>
          <h2>Oyun sesleri</h2>
          <p>MP3 yükle, dinle ve hangi sonuçlarda çalacağını seç.</p>
        </div>
        <div className="sound-toolbar-actions">
          <button className="deactivate" disabled={busy} onClick={()=>void normalize()}>
            <Trash2 /> Atanmamışları temizle
          </button>
          <label className="primary sound-upload">
            <UploadCloud /> {busy ? "İşleniyor…" : "MP3 yükle"}
            <input
              type="file"
              multiple
              accept="audio/mpeg,.mp3"
              disabled={busy}
              onChange={(e) => void upload(Array.from(e.target.files ?? []))}
            />
          </label>
        </div>
      </div>
      {message && <p className="admin-message">{message}</p>}
      <div className="sound-table-wrap">
        <table className="sound-table">
          <thead>
            <tr>
              <th>Ses</th>
              <th>Dinle</th>
              <th>Çok iyi 😂</th>
              <th>Orta 😐</th>
              <th>Çok kötü 👎</th>
              <th>+18</th>
              <th>Aktif</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sounds.map((sound) => (
              <tr key={sound.id}>
                <td><b>{sound.title}</b></td>
                <td>
                  <button className="sound-play" onClick={() => togglePlay(sound)}>
                    {playing === sound.id ? <Square /> : <Play />}
                    {playing === sound.id ? "Durdur" : "Oynat"}
                  </button>
                </td>
                {(["very_good", "meh", "very_bad", "adult", "active"] as const).map((key) => (
                  <td key={key}>
                    <input
                      type="checkbox"
                      checked={Boolean(sound[key])}
                      onChange={(e) => void patch(sound, key, e.target.checked)}
                    />
                  </td>
                ))}
                <td>
                  <button className="sound-delete" onClick={async()=>{if(confirm("Bu ses silinsin mi?")){await fetch(`${api}/sounds/${sound.id}`,{method:"DELETE"});await onSaved()}}}>
                    <Trash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!sounds.length && <div className="admin-empty">Henüz ses yok. İlk MP3 dosyanı yükle.</div>}
      </div>
    </>
  );
}

function CategoryHome({
  rows,
  onSelect,
  adding,
  setAdding,
  onSaved,
}: {
  rows: Category[];
  onSelect: (id: number) => void;
  adding: boolean;
  setAdding: (v: boolean) => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(""),
    [description, setDescription] = useState("");
  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await fetch(`${api}/categories`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, description }),
    });
    if (r.ok) {
      setName("");
      setDescription("");
      setAdding(false);
      await onSaved();
    }
  };
  return (
    <>
      <div className="category-toolbar">
        <div>
          <h2>Meme koleksiyonları</h2>
          <p>Bir kategoriye girerek içindeki memeleri yönet.</p>
        </div>
        <button className="primary" onClick={() => setAdding(!adding)}>
          <Plus /> Kategori ekle
        </button>
      </div>
      {adding && (
        <form className="admin-card quick-category" onSubmit={add}>
          <div>
            <label>Kategori adı</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn. Okul hayatı"
              autoFocus
            />
          </div>
          <div>
            <label>Kısa açıklama</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Oyuncuların göreceği açıklama"
            />
          </div>
          <button className="primary">Oluştur</button>
        </form>
      )}
      <div className="category-cards">
        {rows.map((c, index) => (
          <button
            className="category-card"
            key={c.id}
            onClick={() => onSelect(c.id)}
          >
            <div className={`folder-icon f${index % 5}`}>
              <FolderPlus />
            </div>
            <span className={c.active ? "cat-state active" : "cat-state"}>
              {c.active ? "Oyunda aktif" : "Pasif"}
            </span>
            <h3>{c.name}</h3>
            <p>{c.description || "Henüz açıklama eklenmedi."}</p>
            <footer>
              <b>{c.meme_count} meme</b>
              <ChevronRight />
            </footer>
          </button>
        ))}
        {!rows.length && !adding && (
          <div className="admin-empty">
            Henüz kategori yok. İlk kategorini oluştur.
          </div>
        )}
      </div>
    </>
  );
}

function CategoryDetail({
  category,
  templates,
  onSaved,
}: {
  category: Category;
  templates: Template[];
  onSaved: () => Promise<void>;
}) {
  const [upload, setUpload] = useState(false),
    [editing, setEditing] = useState<Template | null>(null);
  const memes = templates.filter((t) =>
    (t.category_ids ?? "").split(",").includes(String(category.id)),
  );
  const toggle = async () => {
    await fetch(`${api}/categories/${category.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ active: !category.active }),
    });
    await onSaved();
  };
  return (
    <>
      <div className="category-detail-head">
        <div>
          <p>
            {category.description || "Bu kategori için açıklama eklenmedi."}
          </p>
          <span>{memes.length} meme</span>
        </div>
        <div>
          <button
            className={category.active ? "deactivate" : "activate"}
            onClick={toggle}
          >
            {category.active ? "Oyunda gösterme" : "Oyunda göster"}
          </button>
          <button
            className="primary"
            onClick={() => {
              setEditing(null);
              setUpload(!upload);
            }}
          >
            <ImagePlus /> Meme ekle
          </button>
        </div>
      </div>
      {upload && (
        <Uploader
          categoryId={category.id}
          onSaved={async (done) => {
            await onSaved();
            if (done) setUpload(false);
          }}
        />
      )}
      {editing && (
        <Uploader
          categoryId={category.id}
          initialTemplate={editing}
          onSaved={async () => {
            await onSaved();
            setEditing(null);
          }}
        />
      )}
      <div className="archive-grid category-memes">
        {memes.map((t) => (
          <article className="template-card" key={t.id}>
            <img src={`/media/${t.optimized_key}`} alt={t.title} />
            <div>
              <b>{t.title}</b>
              <div className="template-actions">
                <button
                  className="edit-template"
                  onClick={() => {
                    setUpload(false);
                    setEditing(t);
                  }}
                >
                  <Pencil /> Düzenle
                </button>
                <button
                  onClick={async () => {
                    if (confirm("Bu meme silinsin mi?")) {
                      await fetch(`${api}/templates/${t.id}`, {
                        method: "DELETE",
                      });
                      await onSaved();
                    }
                  }}
                >
                  <Trash2 /> Sil
                </button>
              </div>
            </div>
          </article>
        ))}
        {!memes.length && !upload && (
          <div className="admin-empty">
            Bu kategoride henüz meme yok. “Meme ekle” ile başlayabilirsin.
          </div>
        )}
      </div>
    </>
  );
}

function Uploader({
  categoryId,
  onSaved,
  initialTemplate,
}: {
  categoryId: number;
  onSaved: (done: boolean) => Promise<void>;
  initialTemplate?: Template;
}) {
  const [file, setFile] = useState<File | null>(null),
    [queue, setQueue] = useState<File[]>([]),
    [preview, setPreview] = useState(
      initialTemplate ? `/media/${initialTemplate.optimized_key}` : "",
    ),
    [title, setTitle] = useState(initialTemplate?.title ?? ""),
    [zones, setZones] = useState<Zone[]>(
      initialTemplate?.zones?.length ? initialTemplate.zones : [newZone()],
    ),
    [active, setActive] = useState(0),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const imageRef = useRef<HTMLImageElement>(null),
    stageRef = useRef<HTMLDivElement>(null),
    dragRef = useRef<{
      index: number;
      offsetX: number;
      offsetY: number;
    } | null>(null),
    resizeRef = useRef<{
      index: number;
      startX: number;
      startY: number;
      startWidth: number;
      startHeight: number;
    } | null>(null);
  const activeZone = zones[active];
  useEffect(
    () => () => {
      if (preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const pick = (f: File) => {
    if (!f.type.startsWith("image/")) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setTitle(f.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "));
    setZones([newZone()]);
    setActive(0);
  };
  const pickMany = (files: File[]) => {
    const valid = files.filter((f) => f.type.startsWith("image/"));
    if (!valid.length) return;
    setQueue(valid);
    pick(valid[0]);
    setMessage(`${valid.length} meme sıraya alındı.`);
  };
  const addZone = () => {
    setZones((v) => [...v, newZone()]);
    setActive(zones.length);
  };
  const skip = async () => {
    const remaining = queue.slice(1);
    if (remaining.length) {
      setQueue(remaining);
      pick(remaining[0]);
      setMessage(`Bu resim atlandı. Sırada ${remaining.length} meme var.`);
    } else {
      setQueue([]);
      setFile(null);
      setPreview("");
      setTitle("");
      setZones([newZone()]);
      setActive(0);
      await onSaved(true);
    }
  };
  const update = (patch: Partial<Zone>) =>
    setZones((v) => v.map((z, i) => (i === active ? { ...z, ...patch } : z)));
  const encoded = useMemo(() => preview, [preview]);
  const moveZone = (e: React.PointerEvent<HTMLDivElement>) => {
    const stage = stageRef.current;
    if (!stage) return;
    const r = stage.getBoundingClientRect(),
      resize = resizeRef.current;
    if (resize) {
      setZones((v) =>
        v.map((item, i) =>
          i === resize.index
            ? {
                ...item,
                width: Math.max(
                  0.12,
                  Math.min(
                    1 - item.x,
                    resize.startWidth + (e.clientX - resize.startX) / r.width,
                  ),
                ),
                height: Math.max(
                  0.06,
                  Math.min(
                    1 - item.y,
                    resize.startHeight + (e.clientY - resize.startY) / r.height,
                  ),
                ),
              }
            : item,
        ),
      );
      return;
    }
    const drag = dragRef.current;
    if (!drag) return;
    const z = zones[drag.index];
    const x = (e.clientX - r.left - drag.offsetX) / r.width,
      y = (e.clientY - r.top - drag.offsetY) / r.height;
    setZones((v) =>
      v.map((item, i) =>
        i === drag.index
          ? {
              ...item,
              x: Math.max(0, Math.min(1 - z.width, x)),
              y: Math.max(0, Math.min(1 - z.height, y)),
            }
          : item,
      ),
    );
  };
  const save = async () => {
    if (!title || (!file && !initialTemplate))
      return setMessage("Görsel ve başlık gerekli.");
    setBusy(true);
    setMessage("");
    if (initialTemplate) {
      const r = await fetch(`${api}/templates/${initialTemplate.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ title, zones }),
      });
      setBusy(false);
      if (r.ok) await onSaved(true);
      else {
        const d = (await r.json()) as { error: string };
        setMessage(d.error);
      }
      return;
    }
    const currentFile = file!;
    let upload: File,
      width = imageRef.current?.naturalWidth || 1200,
      height = imageRef.current?.naturalHeight || 900;
    if (currentFile.type === "image/gif") {
      upload = currentFile;
    } else {
      const bitmap = await createImageBitmap(currentFile);
      const max = 1600,
        scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas
        .getContext("2d")!
        .drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (v) => (v ? resolve(v) : reject(new Error("Dönüştürülemedi"))),
          "image/webp",
          0.86,
        ),
      );
      upload = new File([blob], "meme.webp", { type: "image/webp" });
      width = canvas.width;
      height = canvas.height;
    }
    const form = new FormData();
    form.append("image", upload);
    form.append(
      "metadata",
      JSON.stringify({
        title,
        width,
        height,
        categoryIds: [categoryId],
        zones,
      }),
    );
    const r = await fetch(`${api}/templates`, { method: "POST", body: form });
    setBusy(false);
    if (r.ok) {
      const remaining = queue.slice(1);
      await onSaved(remaining.length === 0);
      if (remaining.length) {
        setQueue(remaining);
        pick(remaining[0]);
        setMessage(`Kaydedildi. Sırada ${remaining.length} meme var.`);
      }
    } else {
      const d = (await r.json()) as { error: string };
      setMessage(d.error);
    }
  };
  return (
    <div className="editor-layout category-uploader">
      <section className="admin-card editor-main">
        <div className="upload-head">
          <div>
            <h2>{initialTemplate ? "Memeyi düzenle" : "Yeni meme"}</h2>
            <p>
              {initialTemplate
                ? "Kaydedilmiş yazı alanlarını değiştirip tekrar kaydet."
                : "Birden fazla görsel seç; sırayla düzenleyip kaydet."}
            </p>
          </div>
          {preview && (
            <button className="secondary" onClick={addZone}>
              <Plus /> Metin ekle
            </button>
          )}
        </div>
        {!preview ? (
          <label className="dropzone">
            <UploadCloud />
            <b>Görselleri buraya bırak veya seç</b>
            <span>
              Bir veya daha fazla JPG, PNG, WebP, AVIF ya da GIF · dosya başına
              en fazla 8 MB
            </span>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
              onChange={(e) => pickMany(Array.from(e.target.files ?? []))}
            />
          </label>
        ) : (
          <div
            ref={stageRef}
            className="zone-stage"
            onPointerMove={moveZone}
            onPointerUp={() => {
              dragRef.current = null;
              resizeRef.current = null;
            }}
            onPointerCancel={() => {
              dragRef.current = null;
              resizeRef.current = null;
            }}
          >
            <img
              ref={imageRef}
              src={encoded}
              alt="Meme önizleme"
              draggable={false}
            />
            {zones.map((z, i) => (
              <button
                key={z.id}
                className={`zone-box ${active === i ? "active" : ""}`}
                style={{
                  left: `${z.x * 100}%`,
                  top: `${z.y * 100}%`,
                  width: `${z.width * 100}%`,
                  height: `${z.height * 100}%`,
                  color: z.color,
                  WebkitTextStroke: "0",
                  backgroundColor: z.backgroundEnabled
                    ? rgba(z.backgroundColor, z.backgroundOpacity)
                          : "transparent",
                  fontSize: `${Math.min(z.maxFontSize, 36)}px`,
                }}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.currentTarget.setPointerCapture(e.pointerId);
                  const r = stageRef.current!.getBoundingClientRect();
                  dragRef.current = {
                    index: i,
                    offsetX: e.clientX - r.left - z.x * r.width,
                    offsetY: e.clientY - r.top - z.y * r.height,
                  };
                  setActive(i);
                }}
              >
                {z.placeholder}
                <span className="zone-number">{i + 1}</span>
                {active === i && (
                  <span
                    className="zone-resize"
                    title="Boyutlandır"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.setPointerCapture(e.pointerId);
                      dragRef.current = null;
                      resizeRef.current = {
                        index: i,
                        startX: e.clientX,
                        startY: e.clientY,
                        startWidth: z.width,
                        startHeight: z.height,
                      };
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </section>
      <aside className="admin-card properties">
        <h2>Meme ayarları</h2>
        {queue.length > 0 && (
          <div className="batch-queue">
            <b>Toplu yükleme</b>
            <span>Şu an: {queue[0].name}</span>
            <small>
              {queue.length === 1
                ? "Son meme"
                : `Bundan sonra ${queue.length - 1} meme daha var`}
            </small>
          </div>
        )}
        <label>Başlık</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
        {preview && (
          <>
            <hr />
            <button className="add-zone-wide" onClick={addZone}>
              <Plus /> Yeni metin alanı ekle
            </button>
            <div className="zone-title">
              <b>
                Metin alanı {active + 1} / {zones.length}
              </b>
              <button
                className="delete-zone"
                disabled={zones.length === 1}
                title={
                  zones.length === 1
                    ? "En az bir metin alanı gerekli"
                    : undefined
                }
                onClick={() => {
                  setZones((v) => v.filter((_, i) => i !== active));
                  setActive(0);
                }}
              >
                <Trash2 /> Metni sil
              </button>
            </div>
            <label>Örnek metin</label>
            <input
              value={activeZone.placeholder}
              onChange={(e) => update({ placeholder: e.target.value })}
            />
            <div className="two">
              <label>
                Genişlik
                <input
                  type="range"
                  min=".12"
                  max="1"
                  step=".01"
                  value={activeZone.width}
                  onChange={(e) => update({ width: +e.target.value })}
                />
              </label>
              <label>
                Yükseklik
                <input
                  type="range"
                  min=".06"
                  max=".8"
                  step=".01"
                  value={activeZone.height}
                  onChange={(e) => update({ height: +e.target.value })}
                />
              </label>
            </div>
            <div className="resize-tip">
              Kutuyu taşı: ortasından sürükle · Boyutlandır: sağ alt köşedeki
              tutamacı çek
            </div>
            <label className="background-toggle">
              <input
                type="checkbox"
                checked={activeZone.backgroundEnabled}
                onChange={(e) =>
                  update({ backgroundEnabled: e.target.checked })
                }
              />
              <span>Metin alanının arka planını renklendir</span>
            </label>
            <div className="two">
              <label>
                Yazı rengi
                <input
                  type="color"
                  value={activeZone.color}
                  onChange={(e) => update({ color: e.target.value })}
                />
              </label>
              <label>
                Arka plan rengi
                <select
                  disabled={!activeZone.backgroundEnabled}
                  value={activeZone.backgroundColor}
                  onChange={(e) => update({ backgroundColor: e.target.value })}
                >
                  {!backgroundPalette.includes(
                    activeZone.backgroundColor.toLowerCase(),
                  ) && (
                    <option value={activeZone.backgroundColor}>
                      Özel: {activeZone.backgroundColor}
                    </option>
                  )}
                  {backgroundPalette.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {activeZone.backgroundEnabled && (
              <>
                <div className="color-palette">
                  <span>Hazır arka plan renkleri</span>
                  <div>
                    {backgroundPalette.map((color) => (
                      <button
                        key={color}
                        type="button"
                        title={color}
                        aria-label={`Arka plan ${color}`}
                        className={
                          activeZone.backgroundColor.toLowerCase() === color
                            ? "selected"
                            : ""
                        }
                        style={{ backgroundColor: color }}
                        onClick={() => update({ backgroundColor: color })}
                      />
                    ))}
                  </div>
                  <label>
                    HEX renk kodu
                    <input
                      type="text"
                      maxLength={7}
                      value={activeZone.backgroundColor}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^#[0-9a-fA-F]{0,6}$/.test(value))
                          update({ backgroundColor: value });
                      }}
                      onBlur={() => {
                        if (
                          !/^#[0-9a-fA-F]{6}$/.test(
                            activeZone.backgroundColor,
                          )
                        )
                          update({ backgroundColor: "#000000" });
                      }}
                    />
                  </label>
                </div>
                <label className="opacity-control">
                  Arka plan saydamlığı{" "}
                  <b>%{Math.round(activeZone.backgroundOpacity * 100)}</b>
                  <input
                    type="range"
                    min=".1"
                    max="1"
                    step=".05"
                    value={activeZone.backgroundOpacity}
                    onChange={(e) =>
                      update({ backgroundOpacity: +e.target.value })
                    }
                  />
                </label>
              </>
            )}
            <div className="two">
              <label>
                En büyük font
                <input
                  type="number"
                  value={activeZone.maxFontSize}
                  onChange={(e) => update({ maxFontSize: +e.target.value })}
                />
              </label>
              <label>
                Karakter sınırı
                <input
                  type="number"
                  value={activeZone.maxChars}
                  onChange={(e) => update({ maxChars: +e.target.value })}
                />
              </label>
            </div>
          </>
        )}
        <button
          className="primary save-template"
          disabled={busy || !preview}
          onClick={save}
        >
          <Save />{" "}
          {busy
            ? "Kaydediliyor…"
            : initialTemplate
              ? "Değişiklikleri kaydet"
              : queue.length > 1
                ? "Kaydet ve sıradakine geç"
                : "Bu kategoriye ekle"}
        </button>
        {!initialTemplate && queue.length > 0 && (
          <button className="skip-template" disabled={busy} onClick={skip}>
            Bu resmi atla {queue.length > 1 ? "· Sıradakine geç" : ""}
          </button>
        )}
        {message && <p className="admin-message">{message}</p>}
      </aside>
    </div>
  );
}
